import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAppId } from "../../context/AppIdContext";
import 'bootstrap/dist/css/bootstrap.min.css';
import Header from '../../component/header';
import { useNavigate } from 'react-router-dom';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const WalletDashboard = () => {
    // --- Context & State ---
    const { appId } = useAppId();
    const [loading, setLoading] = useState(true);

    // Dashboard Data States
    const [totalMessages, setTotalMessages] = useState(0);
    const [allocatedMessages, setAllocatedMessages] = useState(0);
    const [remainingMessages, setRemainingMessages] = useState(0);
    const [usedMessages, setUsedMessages] = useState(0);
    const [transactions, setTransactions] = useState([]);

    // Modal States
    const [showRechargeModal, setShowRechargeModal] = useState(false);
    const [showAllocateModal, setShowAllocateModal] = useState(false);
    const [showTransactionsModal, setShowTransactionsModal] = useState(false);
    const [rechargeAmount, setRechargeAmount] = useState("");
    const [allocateAmount, setAllocateAmount] = useState("");
    const [selectedUser, setSelectedUser] = useState("");
    const [users, setUsers] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    // --- Fetch Data ---
    useEffect(() => {
        if (appId) {
            fetchWalletData();
            fetchUsers();
        }
    }, [appId]);

    const Navigate = useNavigate();

    const fetchWalletData = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/dis_wtsp_msg_admin_dash`, {
                app_id: appId
            });

            if (response.data.success && response.data.data) {
                const { result, result2, result3 } = response.data.data;

                if (result && result.length > 0) {
                    const balanceData = result[0];
                    setTotalMessages(balanceData.total_messages || 0);
                    setAllocatedMessages(balanceData.total_user_messages || 0);
                    setRemainingMessages((balanceData.total_messages || 0) - (balanceData.total_user_messages || 0));
                }

                if (result2 && result2.length > 0) {
                    setUsedMessages(result2[0].total_message_use || 0);
                }

                if (result3 && result3.length > 0) {
                    setTransactions(result3);
                } else {
                    setTransactions([]);
                }
            }
        } catch (error) {
            console.error("Error fetching wallet data:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await axios.post(`${BASE_URL}/api/users/list`, {
                app_id: appId
            });
            if (response.data.success) {
                setUsers(response.data.data.result || []);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    const handleRecharge = async () => {
        if (!rechargeAmount || rechargeAmount <= 0) {
            alert("Please enter a valid amount");
            return;
        }
        setSubmitting(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/recharge-wallet`, {
                app_id: appId,
                amount: rechargeAmount
            });
            if (response.data.success) {
                alert("Wallet recharged successfully!");
                setShowRechargeModal(false);
                setRechargeAmount("");
                fetchWalletData();
            } else {
                alert(response.data.message || "Recharge failed");
            }
        } catch (error) {
            console.error("Recharge error:", error);
            alert("Error recharging wallet");
        } finally {
            setSubmitting(false);
        }
    };

    const handleAllocate = async () => {
        if (!selectedUser || !allocateAmount || allocateAmount <= 0) {
            alert("Please select a user and enter valid amount");
            return;
        }
        setSubmitting(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/allocate-balance`, {
                app_id: appId,
                user_id: selectedUser,
                amount: allocateAmount
            });
            if (response.data.success) {
                alert("Balance allocated successfully!");
                setShowAllocateModal(false);
                setSelectedUser("");
                setAllocateAmount("");
                fetchWalletData();
            } else {
                alert(response.data.message || "Allocation failed");
            }
        } catch (error) {
            console.error("Allocation error:", error);
            alert("Error allocating balance");
        } finally {
            setSubmitting(false);
        }
    };

    // Helper function to return icon based on transaction type
    const getTransactionIcon = (type) => {
        switch (type) {
            case 'BC':
                return (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                );
            case 'TR':
                return (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="2">
                        <polyline points="16 3 21 3 21 8"></polyline>
                        <line x1="4" y1="20" x2="21" y2="3"></line>
                        <polyline points="21 16 21 21 16 21"></polyline>
                        <line x1="15" y1="15" x2="21" y2="21"></line>
                        <line x1="4" y1="4" x2="9" y2="9"></line>
                    </svg>
                );
            case 'RE':
                return (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="2">
                        <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                        <line x1="2" y1="10" x2="22" y2="10"></line>
                        <line x1="7" y1="15" x2="11" y2="15"></line>
                    </svg>
                );
            default:
                return (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8E92A3" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                );
        }
    };

    // Helper function to process colors and signs dynamically
    const getTransactionFormat = (tx) => {
        let isRed = false;
        let displaySign = '+';
        const titleLower = (tx.title || '').toLowerCase();

        if (titleLower === 'transfer balance') {
            isRed = true;
            displaySign = '-';
        } else if (titleLower === 'remove balance') {
            isRed = false;
            displaySign = '+';
        } else if (tx.type === 'BC') {
            isRed = true;
            displaySign = '-';
        } else if (tx.transfer_qty < 0) {
            isRed = true;
            displaySign = '-';
        }

        return {
            isRed,
            displaySign,
            displayAmount: Math.abs(tx.transfer_qty || 0)
        };
    };

    // Calculate usage percentage
    const usagePercentage = totalMessages > 0 ? Math.round((usedMessages / totalMessages) * 100) : 0;

    if (loading) {
        return (
            <div className="loader-wrapper">
                <div className="spinner"></div>
                <p className="loader-text">Loading Wallet Data...</p>
                <style jsx>{`
                    .loader-wrapper {
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                        min-height: 100vh;
                        width: 100%;
                        background: #F9FAFB;
                    }
                    .spinner {
                        width: 50px;
                        height: 50px;
                        border: 4px solid #F3F4F6;
                        border-top: 4px solid #8B5CF6;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                        margin-bottom: 16px;
                    }
                    .loader-text {
                        color: #6B7280;
                        font-weight: 500;
                        font-size: 1.1rem;
                    }
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div className="wallet-container">
            {/* Recharge Modal */}
            {showRechargeModal && (
                <div className="wallet-modal-overlay" onClick={() => setShowRechargeModal(false)}>
                    <div className="wallet-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="wallet-modal-header">
                            <h3>Add Money to Wallet</h3>
                            <button className="wallet-modal-close" onClick={() => setShowRechargeModal(false)}>×</button>
                        </div>
                        <div className="wallet-modal-body">
                            <label>Amount (₹)</label>
                            <input
                                type="number"
                                className="wallet-modal-input"
                                placeholder="Enter amount"
                                value={rechargeAmount}
                                onChange={(e) => setRechargeAmount(e.target.value)}
                            />
                        </div>
                        <div className="wallet-modal-footer">
                            <button className="wallet-btn-cancel" onClick={() => setShowRechargeModal(false)}>Cancel</button>
                            <button className="wallet-btn-submit" onClick={handleRecharge} disabled={submitting}>
                                {submitting ? "Processing..." : "Add Money"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Allocate Modal */}
            {showAllocateModal && (
                <div className="wallet-modal-overlay" onClick={() => setShowAllocateModal(false)}>
                    <div className="wallet-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="wallet-modal-header">
                            <h3>Allocate Balance</h3>
                            <button className="wallet-modal-close" onClick={() => setShowAllocateModal(false)}>×</button>
                        </div>
                        <div className="wallet-modal-body">
                            <label>Select User</label>
                            <select className="wallet-modal-select" value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
                                <option value="">Select a user</option>
                                {users.map(user => (
                                    <option key={user.user_id} value={user.user_id}>{user.name} ({user.user_type})</option>
                                ))}
                            </select>
                            <label style={{ marginTop: '16px' }}>Amount (₹)</label>
                            <input
                                type="number"
                                className="wallet-modal-input"
                                placeholder="Enter amount"
                                value={allocateAmount}
                                onChange={(e) => setAllocateAmount(e.target.value)}
                            />
                        </div>
                        <div className="wallet-modal-footer">
                            <button className="wallet-btn-cancel" onClick={() => setShowAllocateModal(false)}>Cancel</button>
                            <button className="wallet-btn-submit" onClick={handleAllocate} disabled={submitting}>
                                {submitting ? "Processing..." : "Allocate"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* All Transactions Modal */}
            {showTransactionsModal && (
                <div className="wallet-modal-overlay wallet-modal-large" onClick={() => setShowTransactionsModal(false)}>
                    <div className="wallet-modal-content wallet-modal-large-content" onClick={(e) => e.stopPropagation()}>
                        <div className="wallet-modal-header">
                            <h3>All Transaction Details</h3>
                            <button className="wallet-modal-close" onClick={() => setShowTransactionsModal(false)}>×</button>
                        </div>
                        <div className="wallet-modal-body">
                            <div className="transactions-table-container">
                                <table className="tx-table-full">
                                    <thead>
                                        <tr>
                                            <th>Transaction ID</th>
                                            <th>Title</th>
                                            <th>Type</th>
                                            <th>User</th>
                                            <th style={{ textAlign: 'right' }}>Amount</th>
                                            <th style={{ textAlign: 'right' }}>Date</th>
                                        </tr>
                                        </thead>
                                    <tbody>
                                        {transactions.length > 0 ? (
                                            transactions.map((tx, index) => {
                                                const { isRed, displaySign, displayAmount } = getTransactionFormat(tx);
                                                return (
                                                    <tr key={index}>
                                                        <td className="tx-id-mono">#{tx.id || index + 1} <span className="tx-time">{tx.time || ''}</span>{" "}</td>
                                                        <td className="tx-title-cell">{tx.title || 'Transaction'}</td>
                                                        <td>
                                                            <span className={`tx-badge ${isRed ? 'badge-debit' : 'badge-credit'}`}>
                                                                {isRed ? 'Debit' : 'Credit'}
                                                            </span>
                                                        </td>
                                                        <td className="tx-user">{tx.name || '-'}</td>
                                                        <td className={isRed ? 'text-danger' : 'text-success'} style={{ textAlign: 'right' }}>
                                                            {displaySign} ₹{displayAmount.toLocaleString()}
                                                        </td>
                                                        <td className="tx-date-full" style={{ textAlign: 'right' }}>{tx.transaction_date}</td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>No transactions found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Dashboard */}
            <div className="dashboard-header-wrapper">
                <Header title={"Wallet"} />
            </div>

            <main className="dashboard-main">
                <div className="page-header slide-up-element">
                    <div>
                        <p className="page-subtitle">Whatsapp Message Wallet</p>
                    </div>
                </div>

                {/* Bootstrap Grid System */}
                <div className="row g-3 g-md-4">
                    {/* Left Column */}
                    <div className="col-lg-5 col-md-12 left-column slide-up-element">
                        {/* Balance Card */}
                        <div className="balance-card">
                            <div className="balance-header">
                                <div className="balance-title-group">
                                    <div className="amount-display">
                                        <h2>₹ {totalMessages.toLocaleString()}</h2>
                                        <span className="growth-indicator">
                                            <span className="balance-label">TOTAL BALANCE</span>
                                        </span>
                                    </div>
                                </div>

                                <button className="add-money-btn" onClick={() => setShowRechargeModal(true)}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                                        <line x1="2" y1="10" x2="22" y2="10"></line>
                                        <line x1="7" y1="15" x2="11" y2="15"></line>
                                    </svg>
                                    Add Recharge
                                </button>
                            </div>

                            <div className="row g-3 g-md-4">
                                <div className="col-12 col-md-6">
                                    <div className="sub-balance-item">
                                        <div className="sub-header">
                                            <div className="sub-title">
                                                <div className="small-icon allocated">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                                                        <line x1="7" y1="17" x2="17" y2="7"></line>
                                                        <polyline points="7 7 17 7 17 17"></polyline>
                                                    </svg>
                                                </div>
                                                ALLOCATED
                                            </div>
                                            <div className="sub-amount">₹ {allocatedMessages.toLocaleString()}</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-6">
                                    <div className="sub-balance-item">
                                        <div className="sub-header">
                                            <div className="sub-title">
                                                <div className="small-icon remaining">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                                                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                                                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                                                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                                                    </svg>
                                                </div>
                                                REMAINING
                                            </div>
                                            <div className="sub-amount">₹ {remainingMessages.toLocaleString()}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons Row */}
                        <div className="row g-3">
                            <div className="col-12 col-sm-6">
                                <button className="action-btn btn-purple-light p-4" onClick={() => Navigate('/dis_wtsp_user_wallets')}>
                                    <div className="icon-circle white-icon">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="3" width="7" height="7"></rect>
                                            <rect x="14" y="3" width="7" height="7"></rect>
                                            <rect x="14" y="14" width="7" height="7"></rect>    
                                            <rect x="3" y="14" width="7" height="7"></rect>
                                        </svg>
                                    </div>
                                    <span>Allocate Balance</span>
                                </button>
                            </div>
                            <div className="col-12 col-sm-6">
                                <button className="action-btn btn-white-purple p-4" onClick={() => setShowTransactionsModal(true)}>
                                    <div className="icon-square dark-icon">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M4 6h2v2H4zm0 5h2v2H4zm0 5h2v2H4zm16-8V6H8.023v2H18.8zM8 11h12v2H8zm0 5h12v2H8z"></path>
                                        </svg>
                                    </div>
                                    <span>All Transactions</span>
                                </button>
                            </div>
                        </div>

                        {/* Total Message Balance Use Strip */}
                        <div className="total-balance-strip">
                            <div className="strip-container">
                                <div className="strip-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                                        <path d="M21 16v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2M7 10l5 5 5-5M12 15V3" strokeLinecap="round" />
                                    </svg>
                                </div>
                                <div className="strip-title">Total Message Balance Use</div>
                                <div className="strip-value">{usedMessages.toLocaleString()}</div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Recent Transactions */}
                    <div className="col-lg-7 col-md-12 right-column slide-up-element">
                        <div className="transactions-card">
                            <div className="tx-header">
                                <h3>All Transaction Details</h3>
                                <button className="download-link" onClick={() => setShowTransactionsModal(true)}>
                                    View All
                                </button>
                            </div>

                            <div className="transactions-list">
                                {transactions.length > 0 ? (
                                    transactions.slice(0, 5).map((tx, index) => {
                                        const { isRed, displaySign, displayAmount } = getTransactionFormat(tx);
                                        const amountClass = isRed ? 'tx-amount neg' : 'tx-amount pos';

                                        const dateParts = tx.transaction_date ? tx.transaction_date.split(' ') : ['', '', ''];
                                        const day = dateParts[0] || '';
                                        const month = dateParts[1] ? dateParts[1].replace(',', '') : '';
                                        const year = dateParts[2] || '';

                                        return (
                                            <div key={index} className="transaction-item">
                                                <div className="tx-info-wrapper">
                                                    <div className="tx-icon-container">
                                                        {getTransactionIcon(tx.type)}
                                                    </div>
                                                    <div className="tx-info">
                                                        <div className="tx-title">{tx.title || 'Transaction'}</div>
                                                        <div className="tx-meta">{tx.type} • {tx.name || 'System'}</div>
                                                    </div>
                                                </div>
                                                <div className="tx-right-wrapper">
                                                    <div className="tx-date-compact">
                                                        <div className={amountClass}>{displaySign} ₹{displayAmount.toLocaleString()}</div>
                                                        <div className="tx-date">{day} {month} {year}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="no-transactions">
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#8E92A3" strokeWidth="1.5">
                                            <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <p>No recent transactions</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <style jsx>{`
                /* --- RESPONSIVE CONTAINER --- */
                .dashboard-header-wrapper {
                    padding: 16px 20px;
                }
                
                @media (min-width: 768px) {
                    .dashboard-header-wrapper {
                        padding: 25px 25px 0 25px;
                    }
                }

                /* Total Message Balance Use Strip - Enhanced Styling */
                .total-balance-strip {
                    margin-top: 24px;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 16px;
                    padding: 1px;
                    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.25);
                    transition: all 0.3s ease;
                }

                .total-balance-strip:hover {
                    box-shadow: 0 12px 28px rgba(102, 126, 234, 0.35);
                    transform: translateY(-2px);
                }

                .strip-container {
                    background: white;
                    border-radius: 15px;
                    padding: 16px 20px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    flex-wrap: wrap;
                }

                .strip-icon {
                    width: 40px;
                    height: 40px;
                    background: linear-gradient(135deg, #667eea, #764ba2);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .strip-icon svg {
                    stroke: white;
                    width: 20px;
                    height: 20px;
                }

                .strip-title {
                    font-size: 0.85rem;
                    font-weight: 600;
                    color: #4a5568;
                    letter-spacing: 0.3px;
                    flex-shrink: 0;
                }

                .strip-value {
                    font-size: 1.1rem;
                    font-weight: 800;
                    background: linear-gradient(135deg, #667eea, #764ba2);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    flex-shrink: 0;
                }

                .strip-percentage {
                    padding: 4px 10px;
                    border-radius: 30px;
                    font-size: 0.75rem;
                    font-weight: 700;
                    background: linear-gradient(135deg, #667eea, #764ba2);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    flex-shrink: 0;
                }

                .strip-progress {
                    flex: 1;
                    min-width: 100px;
                    background: #e5e7eb;
                    border-radius: 20px;
                    height: 6px;
                    overflow: hidden;
                }

                .strip-progress-bar {
                    background: linear-gradient(90deg, #667eea, #764ba2);
                    border-radius: 20px;
                    height: 100%;
                    transition: width 0.5s ease;
                    position: relative;
                    overflow: hidden;
                }

                .strip-progress-bar::after {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
                    animation: shimmer 1.5s infinite;
                }

                @keyframes shimmer {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                }

                /* --- KEYFRAMES --- */
                @keyframes slideInUp {
                    from { opacity: 0; transform: translateY(15px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                /* --- GLOBAL STRUCTURE --- */
                .wallet-container {
                    background: #E5E7EB;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                    width: 100%;
                    box-sizing: border-box;
                }
                
                .wallet-container * {
                    box-sizing: border-box;
                }

                /* Entry Animations */
                .slide-up-element {
                    opacity: 0;
                    animation: slideInUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
                .left-column { animation-delay: 0.1s; }
                .right-column { animation-delay: 0.2s; }

                /* --- DASHBOARD MAIN - RESPONSIVE --- */
                .dashboard-main {
                    padding: 16px 20px;
                    max-width: 1400px;
                    margin: 0 auto;
                }
                
                @media (min-width: 768px) {
                    .dashboard-main {
                        padding: 24px 32px;
                    }
                }
                
                @media (min-width: 1024px) {
                    .dashboard-main {
                        padding: 32px;
                    }
                }
                
                .page-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-end;
                    margin-bottom: 20px;
                    flex-wrap: wrap;
                    gap: 12px;
                }
                
                @media (min-width: 768px) {
                    .page-header {
                        margin-bottom: 24px;
                        gap: 16px;
                    }
                }
                
                .page-subtitle {
                    font-size: 0.95rem;
                    font-weight: 700;
                    color: #475569;
                    margin: 0;
                }
                
                @media (min-width: 768px) {
                    .page-subtitle {
                        font-size: 1.1rem;
                    }
                }

                /* --- LEFT COLUMN: BALANCE CARD --- */
                .balance-card {
                    background: linear-gradient(145deg, #FAF5FF 0%, #F3E8FF 100%);
                    border-radius: 20px;
                    padding: 20px;
                    margin-bottom: 20px;
                    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.05);
                    position: relative;
                    overflow: hidden;
                    border: 1px solid rgba(139, 92, 246, 0.1);
                }
                
                @media (min-width: 768px) {
                    .balance-card {
                        padding: 28px;
                        margin-bottom: 24px;
                        border-radius: 24px;
                    }
                }
                
                @media (min-width: 1024px) {
                    .balance-card {
                        padding: 32px;
                    }
                }

                .balance-header {
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 20px;
                    flex-wrap: wrap;
                    gap: 16px;
                }
                
                @media (min-width: 480px) {
                    .balance-header {
                        flex-direction: row;
                        align-items: center;
                    }
                }
                
                .balance-label {
                    font-size: 0.7rem;
                    font-weight: 700;
                    color: #8B5CF6;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                }
                
                .add-money-btn {
                    background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%);
                    color: white;
                    border: none;
                    border-radius: 10px;
                    padding: 8px 16px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.25);
                    width: 100%;
                    justify-content: center;
                }
                
                @media (min-width: 480px) {
                    .add-money-btn {
                        width: auto;
                        padding: 10px 20px;
                        font-size: 0.85rem;
                        gap: 8px;
                    }
                }
                
                .add-money-btn:hover {
                    box-shadow: 0 6px 16px rgba(139, 92, 246, 0.4);
                    transform: translateY(-2px);
                }
                
                .amount-display h2 {
                    font-size: 2rem;
                    font-weight: 800;
                    color: #8B5CF6;
                    margin: 0 0 4px 0;
                    letter-spacing: -1px;
                }
                
                @media (min-width: 768px) {
                    .amount-display h2 {
                        font-size: 2.5rem;
                    }
                }
                
                @media (min-width: 1024px) {
                    .amount-display h2 {
                        font-size: 3rem;
                    }
                }

                /* Sub Balance Items */
                .sub-balance-item {
                    background: #FFFFFF;
                    border-radius: 14px;
                    padding: 14px;
                    border: 1px solid #F1F5F9;
                    transition: all 0.3s ease;
                }
                
                @media (min-width: 768px) {
                    .sub-balance-item {
                        padding: 18px;
                        border-radius: 16px;
                    }
                }
                
                @media (min-width: 1024px) {
                    .sub-balance-item {
                        padding: 20px;
                    }
                }
                
                .sub-balance-item:hover {
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
                    transform: translateY(-2px);
                }

                .sub-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 8px;
                    flex-wrap: wrap;
                    gap: 8px;
                }
                
                .sub-title {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 0.7rem;
                    font-weight: 700;
                    color: #64748B;
                }
                
                @media (min-width: 768px) {
                    .sub-title {
                        font-size: 0.75rem;
                        gap: 8px;
                    }
                }
                
                .small-icon {
                    width: 22px;
                    height: 22px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                }
                
                @media (min-width: 768px) {
                    .small-icon {
                        width: 24px;
                        height: 24px;
                    }
                }
                
                .small-icon.allocated { background: linear-gradient(135deg, #8B5CF6, #7C3AED); }
                .small-icon.remaining { background: linear-gradient(135deg, #14B8A6, #0D9488); }
                
                .sub-amount {
                    font-size: 1rem;
                    font-weight: 800;
                    color: #1E293B;
                }
                
                @media (min-width: 768px) {
                    .sub-amount {
                        font-size: 1.2rem;
                    }
                }
                
                @media (min-width: 1024px) {
                    .sub-amount {
                        font-size: 1.25rem;
                    }
                }

                /* --- ACTION BUTTONS --- */
                .action-btn {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    padding: 12px 16px;
                    border-radius: 14px;
                    font-size: 0.8rem;
                    font-weight: 700;
                    border: none;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                }
                
                @media (min-width: 768px) {
                    .action-btn {
                        padding: 14px 20px;
                        font-size: 0.85rem;
                        gap: 12px;
                    }
                }
                
                .action-btn:hover {
                    transform: translateY(-3px);
                }
                
                .btn-purple-light {
                    background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%);
                    color: white;
                    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.2);
                }
                
                .btn-white-purple {
                    background: #FFFFFF;
                    color: #8B5CF6;
                    border: 1px solid #F1F5F9;
                }
                
                .btn-white-purple:hover { 
                    border-color: #8B5CF6; 
                }
                
                .icon-circle {
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.25);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .icon-square {
                    width: 28px;
                    height: 28px;
                    border-radius: 8px;
                    background: #FAF5FF;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                @media (min-width: 768px) {
                    .icon-circle, .icon-square {
                        width: 32px;
                        height: 32px;
                    }
                }
                
                .dark-icon { color: #8B5CF6; }

                /* --- RIGHT COLUMN: TRANSACTIONS CARD --- */
                .transactions-card {
                    background: #FFFFFF;
                    border-radius: 20px;
                    padding: 20px;
                    height: 100%;
                    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.04);
                }
                
                @media (min-width: 768px) {
                    .transactions-card {
                        padding: 24px 28px;
                        border-radius: 24px;
                    }
                }
                
                @media (min-width: 1024px) {
                    .transactions-card {
                        padding: 24px 32px;
                    }
                }
                
                .tx-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                    flex-wrap: wrap;
                    gap: 10px;
                }
                
                @media (min-width: 768px) {
                    .tx-header {
                        margin-bottom: 24px;
                        gap: 12px;
                    }
                }
                
                .tx-header h3 {
                    font-size: 1.1rem;
                    font-weight: 800;
                    color: #1E293B;
                    margin: 0;
                }
                
                @media (min-width: 768px) {
                    .tx-header h3 {
                        font-size: 1.2rem;
                    }
                }
                
                @media (min-width: 1024px) {
                    .tx-header h3 {
                        font-size: 1.25rem;
                    }
                }
                
                .download-link {
                    background: none;
                    border: none;
                    font-size: 0.75rem;
                    font-weight: 700;
                    color: #8B5CF6;
                    cursor: pointer;
                    padding: 6px 12px;
                    border-radius: 20px;
                    background: #FAF5FF;
                }
                
                @media (min-width: 768px) {
                    .download-link {
                        font-size: 0.8rem;
                        padding: 8px 16px;
                    }
                }
                
                .download-link:hover { color: #7C3AED; background: #EDE9FE; }
                
                .transactions-list {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                
                .transaction-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 12px;
                    background: #FFFFFF;
                    border: 1px solid #F1F5F9;
                    transition: all 0.2s ease;
                    border-radius: 14px;
                    flex-wrap: wrap;
                    gap: 10px;
                }
                
                @media (min-width: 768px) {
                    .transaction-item {
                        padding: 14px 16px;
                        border-radius: 16px;
                        gap: 12px;
                    }
                }
                
                .transaction-item:hover {
                    background: #FAF5FF;
                    border-color: #E2E8F0;
                }
                
                .tx-info-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex: 1;
                    min-width: 150px;
                }
                
                @media (min-width: 768px) {
                    .tx-info-wrapper {
                        gap: 16px;
                        min-width: 180px;
                    }
                }
                
                .tx-icon-container {
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #F8FAFC;
                }
                
                @media (min-width: 768px) {
                    .tx-icon-container {
                        width: 44px;
                        height: 44px;
                        border-radius: 12px;
                    }
                }
                
                .tx-title {
                    font-weight: 700;
                    font-size: 0.85rem;
                    color: #1E293B;
                }
                
                @media (min-width: 768px) {
                    .tx-title {
                        font-size: 0.95rem;
                    }
                }
                
                .tx-meta {
                    font-size: 0.65rem;
                    color: #64748B;
                    margin-top: 2px;
                }
                
                @media (min-width: 768px) {
                    .tx-meta {
                        font-size: 0.75rem;
                        margin-top: 4px;
                    }
                }
                
                .tx-right-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-wrap: wrap;
                }
                
                .tx-amount {
                    font-weight: 800;
                    font-size: 0.85rem;
                }
                
                @media (min-width: 768px) {
                    .tx-amount {
                        font-size: 1rem;
                    }
                }
                
                .tx-amount.pos { color: #14B8A6; }
                .tx-amount.neg { color: #EF4444; }
                
                .tx-date-compact {
                    text-align: right;
                }
                
                .tx-date {
                    font-size: 0.65rem;
                    color: #94A3B8;
                }
                
                @media (min-width: 768px) {
                    .tx-date {
                        font-size: 0.75rem;
                    }
                }
                
                .no-transactions {
                    text-align: center;
                    padding: 30px 16px;
                    color: #94A3B8;
                }
                
                @media (min-width: 768px) {
                    .no-transactions {
                        padding: 40px 20px;
                    }
                }

                /* --- WALLET MODALS - RESPONSIVE --- */
                .wallet-modal-overlay {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(15, 23, 42, 0.6);
                    backdrop-filter: blur(8px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 16px;
                }
                
                .wallet-modal-content {
                    background: #FFFFFF;
                    border-radius: 20px;
                    width: 100%;
                    max-width: 450px;
                    overflow: hidden;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                }
                
                @media (min-width: 768px) {
                    .wallet-modal-content {
                        border-radius: 24px;
                    }
                }
                
                .wallet-modal-large-content { 
                    max-width: 95%;
                    width: 100%;
                }
                
                @media (min-width: 768px) {
                    .wallet-modal-large-content { 
                        max-width: 900px;
                    }
                }
                
                .wallet-modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 18px 20px;
                    border-bottom: 1px solid #F1F5F9;
                    background: #F8FAFC;
                }
                
                @media (min-width: 768px) {
                    .wallet-modal-header {
                        padding: 24px 32px;
                    }
                }
                
                .wallet-modal-header h3 {
                    margin: 0;
                    font-size: 1.1rem;
                    font-weight: 800;
                    color: #1E293B;
                }
                
                @media (min-width: 768px) {
                    .wallet-modal-header h3 {
                        font-size: 1.25rem;
                    }
                }
                
                .wallet-modal-close {
                    background: none;
                    border: none;
                    font-size: 24px;
                    cursor: pointer;
                    color: #94A3B8;
                }
                
                @media (min-width: 768px) {
                    .wallet-modal-close {
                        font-size: 28px;
                    }
                }
                
                .wallet-modal-body { 
                    padding: 20px;
                }
                
                @media (min-width: 768px) {
                    .wallet-modal-body { 
                        padding: 32px;
                    }
                }
                
                .wallet-modal-body label {
                    display: block;
                    font-size: 0.8rem;
                    font-weight: 600;
                    margin-bottom: 6px;
                    color: #475569;
                }
                
                @media (min-width: 768px) {
                    .wallet-modal-body label {
                        font-size: 0.85rem;
                        margin-bottom: 8px;
                    }
                }
                
                .wallet-modal-input, .wallet-modal-select {
                    width: 100%;
                    padding: 10px 14px;
                    border: 2px solid #E2E8F0;
                    border-radius: 12px;
                    font-size: 0.9rem;
                    outline: none;
                }
                
                @media (min-width: 768px) {
                    .wallet-modal-input, .wallet-modal-select {
                        padding: 14px 16px;
                        font-size: 0.95rem;
                    }
                }
                
                .wallet-modal-input:focus, .wallet-modal-select:focus {
                    border-color: #8B5CF6;
                    box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.1);
                }
                
                .wallet-modal-footer {
                    display: flex;
                    gap: 12px;
                    padding: 16px 20px;
                    border-top: 1px solid #F1F5F9;
                    background: #F8FAFC;
                }
                
                @media (min-width: 768px) {
                    .wallet-modal-footer {
                        gap: 16px;
                        padding: 24px 32px;
                    }
                }
                
                .wallet-btn-cancel, .wallet-btn-submit {
                    flex: 1;
                    padding: 10px;
                    border-radius: 12px;
                    font-weight: 700;
                    cursor: pointer;
                    font-size: 0.85rem;
                }
                
                @media (min-width: 768px) {
                    .wallet-btn-cancel, .wallet-btn-submit {
                        padding: 14px;
                        font-size: 0.95rem;
                    }
                }
                
                .wallet-btn-cancel {
                    background: #E2E8F0;
                    border: none;
                    color: #475569;
                }
                
                .wallet-btn-submit {
                    background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%);
                    border: none;
                    color: white;
                }
                
                .wallet-btn-submit:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                /* Full Table Styles - Responsive */
                .transactions-table-container {
                    max-height: 400px;
                    overflow-x: auto;
                    overflow-y: auto;
                }
                
                @media (min-width: 768px) {
                    .transactions-table-container {
                        max-height: 500px;
                    }
                }
                
                .tx-table-full {
                    width: 100%;
                    min-width: 600px;
                    border-collapse: separate;
                    border-spacing: 0;
                }
                
                .tx-table-full th {
                    text-align: left;
                    padding: 10px 12px;
                    font-size: 0.7rem;
                    font-weight: 700;
                    color: #64748B;
                    background: #F8FAFC;
                    border-bottom: 2px solid #E2E8F0;
                    position: sticky;
                    top: 0;
                    z-index: 10;
                }
                
                @media (min-width: 768px) {
                    .tx-table-full th {
                        padding: 16px;
                        font-size: 0.75rem;
                    }
                }
                
                .tx-table-full td {
                    padding: 10px 12px;
                    font-size: 0.8rem;
                    border-bottom: 1px solid #F1F5F9;
                    color: #1E293B;
                }
                
                @media (min-width: 768px) {
                    .tx-table-full td {
                        padding: 16px;
                        font-size: 0.9rem;
                    }
                }
                
                .tx-id-mono {
                    font-family: monospace;
                    font-size: 0.75rem !important;
                    color: #64748B !important;
                }
                
                @media (min-width: 768px) {
                    .tx-id-mono {
                        font-size: 0.85rem !important;
                    }
                }
                
                .tx-time {
                    font-size: 0.65rem;
                    color: #94A3B8;
                    display: block;
                }
                
                @media (min-width: 768px) {
                    .tx-time {
                        font-size: 0.75rem;
                    }
                }
                
                .badge-debit, .badge-credit {
                    padding: 3px 8px;
                    border-radius: 16px;
                    font-size: 0.65rem;
                    font-weight: 700;
                }
                
                @media (min-width: 768px) {
                    .badge-debit, .badge-credit {
                        padding: 4px 12px;
                        font-size: 0.7rem;
                    }
                }
                
                .badge-debit {
                    background: rgba(239, 68, 68, 0.1);
                    color: #EF4444;
                }
                
                .badge-credit {
                    background: rgba(20, 184, 166, 0.1);
                    color: #14B8A6;
                }

                /* Mobile Specific Adjustments */
                @media (max-width: 576px) {
                    .strip-container {
                        flex-direction: column;
                        text-align: center;
                        align-items: stretch;
                    }
                    
                    .strip-icon {
                        margin: 0 auto;
                    }
                    
                    .strip-progress {
                        width: 100%;
                    }
                    
                    .strip-value, .strip-percentage {
                        text-align: center;
                    }
                    
                    .row.g-3 {
                        --bs-gutter-y: 1rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default WalletDashboard;